import { useRef, type KeyboardEvent, type ReactNode } from "react";
import "./Tabs.css";

export interface TabItem {
  id: string;
  label: string;
}

interface TabsProps {
  items: TabItem[];
  activeId: string;
  onChange: (id: string) => void;
  ariaLabel: string;
}

/** A minimal WAI-ARIA tabs widget: arrow-key roving focus between tabs,
 * Home/End to jump to the first/last, native buttons for click/Enter/Space. */
export function Tabs({ items, activeId, onChange, ariaLabel }: TabsProps) {
  const buttonRefs = useRef<Record<string, HTMLButtonElement | null>>({});

  function focusAndSelect(id: string) {
    onChange(id);
    buttonRefs.current[id]?.focus();
  }

  function handleKeyDown(event: KeyboardEvent<HTMLDivElement>) {
    const currentIndex = items.findIndex((t) => t.id === activeId);
    if (currentIndex === -1) return;

    if (event.key === "ArrowRight" || event.key === "ArrowLeft") {
      event.preventDefault();
      const direction = event.key === "ArrowRight" ? 1 : -1;
      const nextIndex = (currentIndex + direction + items.length) % items.length;
      focusAndSelect(items[nextIndex].id);
    } else if (event.key === "Home") {
      event.preventDefault();
      focusAndSelect(items[0].id);
    } else if (event.key === "End") {
      event.preventDefault();
      focusAndSelect(items[items.length - 1].id);
    }
  }

  return (
    // The WAI-ARIA APG tablist pattern uses roving tabindex on the tab
    // buttons themselves (tabIndex 0/-1 below) — the tablist container isn't
    // meant to be a separate focus stop, only the active tab is.
    // eslint-disable-next-line jsx-a11y/interactive-supports-focus
    <div className="tabs" role="tablist" aria-label={ariaLabel} onKeyDown={handleKeyDown}>
      {items.map((item) => {
        const isActive = item.id === activeId;
        return (
          <button
            key={item.id}
            ref={(el) => {
              buttonRefs.current[item.id] = el;
            }}
            type="button"
            role="tab"
            id={`tab-${item.id}`}
            aria-selected={isActive}
            aria-controls={`tabpanel-${item.id}`}
            tabIndex={isActive ? 0 : -1}
            className={`tabs__tab${isActive ? " is-active" : ""}`}
            onClick={() => onChange(item.id)}
          >
            {item.label}
          </button>
        );
      })}
    </div>
  );
}

export function TabPanel({
  id,
  activeId,
  children,
}: {
  id: string;
  activeId: string;
  children: ReactNode;
}) {
  if (id !== activeId) return null;
  return (
    <div role="tabpanel" id={`tabpanel-${id}`} aria-labelledby={`tab-${id}`} tabIndex={0} className="tabs__panel">
      {children}
    </div>
  );
}
